/**
 * Bootstrap idempotente da estrutura mínima do usuário autenticado.
 *
 * MUST HAVE (bloqueia a UI autenticada até existir):
 * - users/{uid}
 * - publicProfiles/{uid}
 * - workspaces/{uid} (pessoal) + members/{uid}
 *
 * BEST EFFORT (não bloqueia LegalConsentModal):
 * - callable repairUserWorkspaceFields (IDs legados / Admin SDK)
 *
 * Workspace pessoal: workspaceId === uid. Leitura membership-gated pode
 * devolver permission-denied para usuário novo; o fallback cliente tenta
 * criar a estrutura já autorizada pelas Rules, sem overwrite de plan/billing.
 */

import {
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { DEFAULT_BILLING } from "@/config/billing";
import { buildMarketingPreferencesPayload } from "@/services/users/marketingPreferences";
import { buildSocialLinksPayload } from "@/services/users/userMappers";
import { repairUserWorkspaceFields } from "@/services/users/repairUserWorkspaceFields";
import { analyzeUserStructureGaps } from "@/utils/userStructure";
import {
  buildPersonalWorkspaceMemberPayload,
  buildPersonalWorkspacePayload,
  getPersonalWorkspaceRefs,
} from "@/services/workspaces/workspaceService";

/** @type {Map<string, Promise<unknown>>} */
const inFlightByUid = new Map();

const TRANSIENT_REPAIR_CODES = new Set([
  "unavailable",
  "internal",
  "deadline-exceeded",
  "resource-exhausted",
  "aborted",
]);

const STRUCTURAL_REPAIR_CODES = new Set([
  "unauthenticated",
  "permission-denied",
  "invalid-argument",
  "failed-precondition",
]);

/**
 * @param {string} uid
 * @returns {string}
 */
function abbreviateUid(uid) {
  if (typeof uid !== "string" || uid.length <= 8) {
    return uid;
  }

  return `${uid.slice(0, 6)}…`;
}

/**
 * @param {string} event
 * @param {string} [detail]
 */
function logBootstrap(event, detail = "") {
  if (process.env.NODE_ENV !== "development") {
    return;
  }

  console.info(`[user-bootstrap] ${event}${detail}`);
}

/**
 * @param {unknown} error
 * @returns {string}
 */
export function getCallableErrorCode(error) {
  if (error == null || typeof error !== "object" || !("code" in error)) {
    return "";
  }

  return String(error.code).replace(/^functions\//, "").trim();
}

/**
 * Falha temporária da callable / runtime local — não impede o bootstrap mínimo.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isTransientRepairError(error) {
  const code = getCallableErrorCode(error);

  if (TRANSIENT_REPAIR_CODES.has(code)) {
    return true;
  }

  if (code === "network-request-failed") {
    return true;
  }

  const message = error != null && typeof error === "object" && "message" in error
    ? String(error.message)
    : String(error ?? "");

  return /failed to load function/i.test(message)
    || /failed to fetch/i.test(message);
}

/**
 * Erro estrutural da callable — classificado; só bloqueia se o MUST HAVE falhar.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isStructuralRepairError(error) {
  return STRUCTURAL_REPAIR_CODES.has(getCallableErrorCode(error));
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isPermissionDenied(error) {
  return getCallableErrorCode(error) === "permission-denied";
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isIdempotentWorkspaceWriteError(error) {
  const code = getCallableErrorCode(error);
  return code === "already-exists" || code === "permission-denied";
}

/**
 * @param {import("firebase/firestore").DocumentReference} ref
 * @returns {Promise<{ exists: boolean, data: Record<string, unknown> | null, denied: boolean }>}
 */
async function readDocSafe(ref) {
  try {
    const snapshot = await getDoc(ref);

    return {
      exists: snapshot.exists(),
      data: snapshot.exists() ? /** @type {Record<string, unknown>} */ (snapshot.data()) : null,
      denied: false,
    };
  } catch (error) {
    if (isPermissionDenied(error)) {
      return { exists: false, data: null, denied: true };
    }

    throw error;
  }
}

/**
 * @param {string} userId
 * @returns {Promise<({ id: string } & import("firebase/firestore").DocumentData) | null>}
 */
async function readUserDoc(userId) {
  const snapshot = await getDoc(doc(db, "users", userId));

  if (!snapshot.exists()) {
    return null;
  }

  return { id: userId, ...snapshot.data() };
}

/**
 * Cria workspace pessoal + member sem overwrite de docs existentes.
 *
 * permission-denied / already-exists no write, após leitura denied, é
 * tratado como idempotente (o doc provavelmente já existe).
 *
 * @param {string} userId
 * @param {string} displayName
 * @param {{
 *   needsWorkspace: boolean,
 *   needsMember: boolean,
 *   fromDeniedRead: boolean,
 * }} flags
 * @returns {Promise<string[]>}
 */
async function attemptClientWorkspaceBootstrap(userId, displayName, flags) {
  if (!flags.needsWorkspace && !flags.needsMember) {
    return [];
  }

  const { workspaceRef, memberRef } = getPersonalWorkspaceRefs(userId);
  const batch = writeBatch(db);
  /** @type {string[]} */
  const repaired = [];

  if (flags.needsWorkspace) {
    batch.set(workspaceRef, buildPersonalWorkspacePayload(userId, displayName));
    repaired.push("workspaces");
  }

  if (flags.needsMember) {
    batch.set(memberRef, buildPersonalWorkspaceMemberPayload(userId));
    repaired.push("workspaces/members");
  }

  try {
    await batch.commit();
    return repaired;
  } catch (error) {
    if (flags.fromDeniedRead && isIdempotentWorkspaceWriteError(error)) {
      return [];
    }

    throw error;
  }
}

/**
 * @returns {Promise<{
 *   repaired?: boolean,
 *   fields?: string[],
 * } | null>}
 */
async function runRepairBestEffort() {
  logBootstrap("repair start");

  try {
    const serverResult = await repairUserWorkspaceFields();
    return serverResult;
  } catch (error) {
    const code = getCallableErrorCode(error) || "unknown";

    if (isTransientRepairError(error)) {
      logBootstrap("repair failed-soft", ` code=${code}`);

      if (process.env.NODE_ENV === "development") {
        console.warn(
          "[FIVI360] repairUserWorkspaceFields unavailable; continuing with minimal structure:",
          error,
        );
      }

      return null;
    }

    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[FIVI360] repairUserWorkspaceFields failed (${isStructuralRepairError(error) ? "structural" : "unclassified"} code=${code}):`,
        error,
      );
    }

    if (isStructuralRepairError(error)) {
      logBootstrap("repair failed-soft", ` code=${code}`);
      return null;
    }

    logBootstrap("repair failed-soft", ` code=${code}`);
    return null;
  }
}

/**
 * @param {string} userId
 * @param {{
 *   displayName?: string,
 *   email?: string,
 *   skipServerWorkspaceRepair?: boolean,
 *   marketingConsentSource?: string,
 * }} [options]
 */
async function ensureUserStructureUncached(
  userId,
  {
    displayName = "",
    email = "",
    skipServerWorkspaceRepair = false,
    marketingConsentSource = "google_signup_default",
  } = {},
) {
  logBootstrap("start", ` uid=${abbreviateUid(userId)}`);

  const userRef = doc(db, "users", userId);
  const publicProfileRef = doc(db, "publicProfiles", userId);
  const { workspaceId, workspaceRef, memberRef } = getPersonalWorkspaceRefs(userId);
  const isPersonalWorkspace = workspaceId === userId;

  const [userRead, publicRead, workspaceRead, memberRead] = await Promise.all([
    readDocSafe(userRef),
    readDocSafe(publicProfileRef),
    readDocSafe(workspaceRef),
    readDocSafe(memberRef),
  ]);

  const gaps = analyzeUserStructureGaps({
    userId,
    userExists: userRead.exists,
    userData: userRead.data,
    publicProfileExists: publicRead.exists,
    workspaceExists: workspaceRead.exists,
    memberExists: memberRead.exists,
  });

  if (workspaceRead.denied) {
    gaps.needsWorkspace = true;
  }

  if (memberRead.denied) {
    gaps.needsMember = true;
  }

  const resolvedDisplayName =
    (typeof displayName === "string" && displayName.trim())
    || (typeof userRead.data?.displayName === "string" ? userRead.data.displayName : "")
    || (typeof publicRead.data?.displayName === "string" ? publicRead.data.displayName : "")
    || "";

  const resolvedEmail =
    (typeof email === "string" && email.trim())
    || (typeof userRead.data?.email === "string" ? userRead.data.email : "")
    || "";

  const batch = writeBatch(db);
  /** @type {string[]} */
  const repaired = [];

  if (gaps.needsUser) {
    const marketingTs = serverTimestamp();
    batch.set(userRef, {
      displayName: resolvedDisplayName,
      email: resolvedEmail,
      plan: "starter",
      billing: { ...DEFAULT_BILLING },
      defaultWorkspaceId: workspaceId,
      activeWorkspaceId: workspaceId,
      welcomeEmailQueuedAt: null,
      // Sem formulário de marketing neste fluxo (ex.: Google) — nunca presume consentimento.
      marketingPreferences: buildMarketingPreferencesPayload({
        enabled: false,
        consentSource: marketingConsentSource,
        timestamp: marketingTs,
      }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    repaired.push("users");
  }

  if (gaps.needsPublicProfile) {
    batch.set(publicProfileRef, {
      uid: userId,
      slug: "",
      portfolioEnabled: false,
      portfolioAvailable: false,
      displayName: resolvedDisplayName,
      companyName: "",
      companyLogo: "",
      bio: "",
      socialLinks: buildSocialLinksPayload(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    repaired.push("publicProfiles");
  }

  if (repaired.length > 0) {
    await batch.commit();
  }

  const fromDeniedRead = workspaceRead.denied || memberRead.denied;
  let workspaceReady = workspaceRead.exists;
  let memberReady = memberRead.exists;

  if (isPersonalWorkspace && (gaps.needsWorkspace || gaps.needsMember)) {
    const fallbackRepaired = await attemptClientWorkspaceBootstrap(
      userId,
      resolvedDisplayName,
      {
        needsWorkspace: gaps.needsWorkspace && (workspaceRead.denied || !workspaceRead.exists),
        needsMember: gaps.needsMember && (memberRead.denied || !memberRead.exists),
        fromDeniedRead,
      },
    );

    if (fallbackRepaired.includes("workspaces")) {
      workspaceReady = true;
      repaired.push("workspaces");
    } else if (fromDeniedRead && gaps.needsWorkspace) {
      // Write idempotente após denied ⇒ documento provavelmente já existe.
      workspaceReady = true;
    }

    if (fallbackRepaired.includes("workspaces/members")) {
      memberReady = true;
      repaired.push("workspaces/members");
    } else if (fromDeniedRead && gaps.needsMember) {
      memberReady = true;
    }
  }

  const mustHaveReady = Boolean(
    (userRead.exists || repaired.includes("users"))
    && (publicRead.exists || repaired.includes("publicProfiles"))
    && workspaceReady
    && memberReady,
  );

  if (mustHaveReady) {
    logBootstrap("minimal structure ready");
  }

  const stillNeedsWorkspace = gaps.needsWorkspace && !workspaceReady;
  const stillNeedsMember = gaps.needsMember && !memberReady;
  const needsBestEffortRepair =
    stillNeedsWorkspace
    || stillNeedsMember
    || gaps.needsServerWorkspaceIdRepair;

  if (needsBestEffortRepair && !skipServerWorkspaceRepair) {
    if (mustHaveReady) {
      void runRepairBestEffort();
    } else {
      const serverResult = await runRepairBestEffort();

      if (serverResult?.fields?.length) {
        repaired.push(...serverResult.fields);
        workspaceReady = true;
        memberReady = true;
      } else if (serverResult?.repaired) {
        repaired.push("workspaces", "workspaces/members", "users.workspaceIds");
        workspaceReady = true;
        memberReady = true;
      }
    }
  }

  const profile = await readUserDoc(userId);

  if (!profile) {
    const missing = new Error(
      "ensureUserStructure: perfil de usuário não encontrado após o bootstrap.",
    );
    missing.code = "not-found";
    throw missing;
  }

  if (
    (gaps.needsWorkspace && !workspaceReady)
    || (gaps.needsMember && !memberReady)
  ) {
    const missing = new Error(
      "ensureUserStructure: não foi possível criar a estrutura mínima do workspace pessoal.",
    );
    missing.code = "failed-precondition";
    throw missing;
  }

  logBootstrap("done", ` uid=${abbreviateUid(userId)}`);

  return {
    profile,
    gaps,
    repaired: [...new Set(repaired)],
  };
}

/**
 * @param {string} userId
 * @param {{
 *   displayName?: string,
 *   email?: string,
 *   skipServerWorkspaceRepair?: boolean,
 *   marketingConsentSource?: string,
 * }} [options]
 * @returns {Promise<{
 *   profile: Awaited<ReturnType<typeof readUserDoc>>,
 *   gaps: ReturnType<typeof analyzeUserStructureGaps>,
 *   repaired: string[],
 * }>}
 */
export async function ensureUserStructure(userId, options = {}) {
  if (!userId) {
    throw new Error("ensureUserStructure: userId é obrigatório.");
  }

  const existing = inFlightByUid.get(userId);

  if (existing) {
    logBootstrap("reuse in-flight", ` uid=${abbreviateUid(userId)}`);
    return /** @type {ReturnType<typeof ensureUserStructureUncached>} */ (existing);
  }

  const pending = ensureUserStructureUncached(userId, options).finally(() => {
    if (inFlightByUid.get(userId) === pending) {
      inFlightByUid.delete(userId);
    }
  });

  inFlightByUid.set(userId, pending);
  return pending;
}

/** Limpa o single-flight (testes). */
export function resetEnsureUserStructureInflight() {
  inFlightByUid.clear();
}
